import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { createPortal } from 'react-dom';
import { aplicarTema } from './tema';
import { APP_BASE, rotaApp } from '../../pages/comprador/util';
import { limparSessaoComprador } from '../../lib/compradorAuth';
import { InstalarAppBanner } from './InstalarAppBanner';
import { useManifestComprador } from '../../lib/manifestComprador';

/**
 * Casco do app do comprador LOGADO — app de verdade, não site que instala.
 *  - Barra inferior ENXUTA em 4: Início, Obra, Conversa, Perfil (fixa no mobile;
 *    vira coluna lateral no desktop).
 *  - Header com HAMBÚRGUER que abre um drawer lateral com o resto: Parcelas,
 *    Aprender, Notícias (em breve) e, no rodapé, Sair. O drawer desliza SOBRE o
 *    conteúdo (não empurra) e fecha ao tocar fora ou num item.
 *  - Sem "Sair"/"criar senha" soltos no topo (foram para o Perfil / drawer).
 * O ícone da seção ativa assume a cor da marca (--brand). Tema white-label
 * aplicado UMA vez aqui (ver tema.ts).
 */

type IconeNome = 'inicio' | 'obra' | 'conversa' | 'perfil' | 'parcelas' | 'aprender' | 'noticias';

function Icone({ nome, cor, tamanho = 22 }: { nome: IconeNome; cor: string; tamanho?: number }) {
  const s = { stroke: cor, strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };
  const svg = (children: React.ReactNode) => (
    <svg viewBox="0 0 24 24" width={tamanho} height={tamanho} aria-hidden="true">
      {children}
    </svg>
  );
  switch (nome) {
    case 'inicio':
      return svg(<path d="M4 11l8-7 8 7v9a1 1 0 01-1 1h-5v-6h-4v6H5a1 1 0 01-1-1z" {...s} />);
    case 'obra':
      return svg(<><path d="M5 20V9.5L12 4l7 5.5V20" {...s} /><path d="M9 20v-5h6v5" {...s} /></>);
    case 'conversa':
      return svg(<path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H9l-5 4z" {...s} />);
    case 'perfil':
      return svg(<><circle cx="12" cy="8.5" r="3.5" {...s} /><path d="M5 20a7 7 0 0114 0" {...s} /></>);
    case 'parcelas':
      return svg(<><rect x="3" y="6" width="18" height="13" rx="2.5" {...s} /><path d="M3 10h18" {...s} /></>);
    case 'aprender':
      return svg(<path d="M4 5.5A1.5 1.5 0 015.5 4H11v15H5.5A1.5 1.5 0 014 17.5v-12zM20 5.5A1.5 1.5 0 0018.5 4H13v15h5.5a1.5 1.5 0 001.5-1.5v-12z" {...s} />);
    case 'noticias':
      return svg(<><rect x="4" y="4" width="16" height="16" rx="2" {...s} /><path d="M8 9h8M8 13h8M8 17h5" {...s} /></>);
  }
}

const NAV_INFERIOR = [
  { chave: 'inicio', rotulo: 'Início', sufixo: '' },
  { chave: 'obra', rotulo: 'Obra', sufixo: '/obra' },
  { chave: 'conversa', rotulo: 'Conversa', sufixo: '/conversa' },
  { chave: 'perfil', rotulo: 'Perfil', sufixo: '/perfil' },
] as const;

const DRAWER_ITENS = [
  { chave: 'parcelas', rotulo: 'Parcelas', descricao: 'Suas parcelas e boletos', sufixo: '/parcelas', emBreve: false },
  { chave: 'aprender', rotulo: 'Aprender', descricao: 'Dicas e educação financeira', sufixo: '/aprender', emBreve: false },
  { chave: 'noticias', rotulo: 'Notícias', descricao: 'Novidades da incorporadora', sufixo: '', emBreve: true },
] as const;

function BarraNavegacao({ slug }: { slug: string }) {
  const [location] = useLocation();
  const base = `${APP_BASE}/${slug}`;
  const ativo = (sufixo: string) =>
    sufixo === '' ? location === base || location === `${base}/` : location.startsWith(base + sufixo);

  return (
    <nav
      aria-label="Navegação do aplicativo"
      className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface md:inset-x-auto md:inset-y-0 md:left-0 md:w-[88px] md:border-r md:border-t-0"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="mx-auto flex h-16 max-w-md items-stretch justify-around md:h-auto md:max-w-none md:flex-col md:justify-start md:gap-1 md:px-2 md:py-5">
        {NAV_INFERIOR.map((item) => {
          const on = ativo(item.sufixo);
          const cor = on ? 'var(--brand)' : 'var(--ink-3)';
          return (
            <Link
              key={item.chave}
              href={`${base}${item.sufixo}`}
              className="flex flex-1 flex-col items-center justify-center gap-0.5 md:flex-none md:rounded-xl md:py-2.5"
              aria-current={on ? 'page' : undefined}
              data-testid={`nav-${item.chave}`}
            >
              <Icone nome={item.chave} cor={cor} />
              <span className="text-[10.5px] font-medium" style={{ color: cor }}>
                {item.rotulo}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

/** Drawer lateral (menu hambúrguer): desliza SOBRE o conteúdo; fecha ao tocar
 *  fora ou num item. Renderizado num portal para não ser cortado por ancestrais. */
function Drawer({
  slug,
  aberto,
  onFechar,
  onSair,
}: {
  slug: string;
  aberto: boolean;
  onFechar: () => void;
  onSair: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onFechar();
    }
    if (aberto) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [aberto, onFechar]);

  if (typeof document === 'undefined') return null;
  const base = `${APP_BASE}/${slug}`;

  return createPortal(
    <div
      className={`fixed inset-0 z-[60] transition-opacity duration-200 ${aberto ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      aria-hidden={!aberto}
    >
      {/* Overlay: toque fora fecha. */}
      <div className="absolute inset-0 bg-black/40" onClick={onFechar} data-testid="drawer-overlay" />
      {/* Painel: desliza da esquerda, SOBRE o conteúdo. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        data-testid="comprador-drawer"
        className={`absolute inset-y-0 left-0 flex w-[82vw] max-w-[320px] flex-col bg-surface shadow-2xl transition-transform duration-200 ${aberto ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <span className="text-[15px] font-semibold text-ink">Menu</span>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar menu"
            className="-mr-1 rounded-full p-1.5 text-ink-3 hover:bg-bg-warm hover:text-ink"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3">
          {DRAWER_ITENS.map((item) =>
            item.emBreve ? (
              <div
                key={item.chave}
                className="flex items-center gap-3 rounded-xl px-3 py-3 opacity-60"
                data-testid={`drawer-${item.chave}`}
              >
                <Icone nome={item.chave} cor="var(--ink-3)" tamanho={20} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-medium text-ink">{item.rotulo}</span>
                  <span className="block text-[12px] text-ink-4">{item.descricao}</span>
                </span>
                <span className="rounded-full bg-bg-warm px-2 py-0.5 text-[10.5px] font-semibold text-ink-3">em breve</span>
              </div>
            ) : (
              <Link
                key={item.chave}
                href={`${base}${item.sufixo}`}
                onClick={onFechar}
                data-testid={`drawer-${item.chave}`}
                className="flex items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-bg-warm"
              >
                <Icone nome={item.chave} cor="var(--brand)" tamanho={20} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-medium text-ink">{item.rotulo}</span>
                  <span className="block text-[12px] text-ink-4">{item.descricao}</span>
                </span>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" className="text-ink-4" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            ),
          )}
        </nav>

        <div className="border-t border-line p-3">
          <button
            type="button"
            onClick={onSair}
            data-testid="drawer-sair"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-bg-warm"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" className="text-ink-3" aria-hidden="true">
              <path d="M15 12H4m0 0l3.5-3.5M4 12l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M11 6V5a2 2 0 012-2h4a2 2 0 012 2v14a2 2 0 01-2 2h-4a2 2 0 01-2-2v-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[14px] font-medium text-ink">Sair</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function AppShellComprador({
  children,
  corMarca,
  orgNome,
  semPadding,
}: {
  children: React.ReactNode;
  corMarca: string | null | undefined;
  /** Nome da incorporadora — para o convite de instalação white-label. */
  orgNome?: string | null;
  /** Telas que controlam o próprio layout até as bordas (ex.: Conversa). */
  semPadding?: boolean;
}) {
  const { slug } = useParams<{ slug: string }>();
  const [location] = useLocation();
  const raiz = useRef<HTMLDivElement>(null);
  const [drawerAberto, setDrawerAberto] = useState(false);
  // O CTA de instalação (bloco de destaque) aparece só na Início — proativo,
  // sem repetir o bloco grande em toda aba.
  const ehHome = !!slug && (location === `${APP_BASE}/${slug}` || location === `${APP_BASE}/${slug}/`);

  // Logout explícito: limpa a sessão e faz navegação DURA para o login.
  function sair() {
    if (!slug) return;
    limparSessaoComprador(slug);
    window.location.assign(rotaApp(slug));
  }

  useEffect(() => {
    if (raiz.current) aplicarTema(raiz.current, corMarca);
  }, [corMarca]);

  // Identidade PWA da incorporadora (manifest/ícone/theme-color por slug).
  useManifestComprador(slug, corMarca);

  return (
    <div ref={raiz} className="flex min-h-dvh flex-col overflow-x-hidden bg-bg-warm text-ink">
      {/* Header slim: só o hambúrguer (abre o drawer). Sem Sair/senha soltos. */}
      <header className="sticky top-0 z-30 flex h-12 items-center bg-bg-warm/85 px-3 backdrop-blur md:hidden" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <button
          type="button"
          onClick={() => setDrawerAberto(true)}
          aria-label="Abrir menu"
          data-testid="comprador-hamburguer"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-surface"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      <div className="flex-1 pb-16 md:pb-0 md:pl-[88px]">
        <div className={semPadding ? 'mx-auto flex h-full max-w-[720px] flex-col' : 'mx-auto max-w-[720px] px-5 pb-6 pt-1'}>
          {!semPadding && ehHome && slug && <InstalarAppBanner slug={slug} orgNome={orgNome} corMarca={corMarca} />}
          {children}
        </div>
      </div>

      {slug && <BarraNavegacao slug={slug} />}
      {slug && <Drawer slug={slug} aberto={drawerAberto} onFechar={() => setDrawerAberto(false)} onSair={sair} />}
    </div>
  );
}
